import type { IBranch } from '@/interfaces/IBranch';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import { isAvailableHere } from './availability';

/**
 * «Lo más pedido» on the Menú page (Storefront V2 D-ADM-4, D-PO-1): the
 * storefront ranks each branch's products every night from its last 30 days
 * of orders; the owner's switch decides whether the storefront shows them,
 * and each product or category can be kept out of it (owner 2026-11-03).
 */
type IPopularBranches =
  | ReadonlyArray<Pick<IBranch, 'popularItemIds'>>
  | undefined;

/** The storefront's limits (cartadigital-app shared/storefront/popular.ts). */
export const POPULAR_MAX = 8;
/** At most one ranked product for every 4 products of the menu shown. */
export const POPULAR_DISHES_PER_BADGE = 4;

/** Some branch has enough recent orders for the storefront to rank products (D-PO-1). */
export function hasPopularData(branches: IPopularBranches): boolean {
  return (branches ?? []).some(
    (branch) => (branch.popularItemIds?.length ?? 0) > 0
  );
}

/** The card shows when it can matter: data exists, or the owner turned it off (D-ADM-4). */
export function popularCardVisible(input: {
  branches: IPopularBranches;
  showPopular: boolean | undefined;
}): boolean {
  return input.showPopular === false || hasPopularData(input.branches);
}

/** The editors' «Lo más pedido» switch shows while the storefront shows a ranking. */
export function popularControlsVisible(input: {
  branches: IPopularBranches;
  showPopular: boolean | undefined;
}): boolean {
  return input.showPopular !== false && hasPopularData(input.branches);
}

/** The ranking the list shows: the admin's branch's, else the first branch that has one. */
export function popularBranchOf<T extends Pick<IBranch, 'id' | 'popularItemIds'>>(
  branches: readonly T[] | undefined,
  branchId: number | null
): T | null {
  const ranked = (branches ?? []).filter(
    (branch) => (branch.popularItemIds?.length ?? 0) > 0
  );
  return ranked.find((branch) => branch.id === branchId) ?? ranked[0] ?? null;
}

interface IPopularRow {
  item: IItem;
  category: ICategory | null;
}

export interface IPopularEntry {
  item: IItem;
  category: ICategory;
  /** Its «Nº n más pedido», from 1. */
  rank: number;
}

/** Kept out by the owner, itself or through its category. */
export function popularHidden(item: IItem, category: ICategory | null): boolean {
  return !!item.popularHidden || !!category?.popularHidden;
}

/** A product diners can see here: visible, in a visible category that is not POS-only, in this branch. */
function shownToDiners(
  row: IPopularRow,
  branchId: number | null
): row is { item: IItem; category: ICategory } {
  return (
    !!row.category &&
    !row.category.posOnly &&
    isAvailableHere(row.category, branchId) &&
    isAvailableHere(row.item, branchId)
  );
}

/**
 * «Lo más pedido» as the storefront shows it for this branch and menu
 * (selectPopular of cartadigital-app): the best ranked products diners can
 * see, the ones kept out skipped, at most one for every 4 products shown and
 * 8 in all, numbered from 1. Schedules are left out: they change with the
 * hour.
 */
export function popularRanking(input: {
  rankedIds: readonly number[];
  /** The menu's products with their categories (the Menú page's rows). */
  rows: readonly IPopularRow[];
  branchId: number | null;
}): IPopularEntry[] {
  const shown = input.rows.filter(
    (row): row is { item: IItem; category: ICategory } =>
      shownToDiners(row, input.branchId)
  );
  const limit = Math.min(
    POPULAR_MAX,
    Math.floor(shown.length / POPULAR_DISHES_PER_BADGE)
  );
  const byId = new Map(shown.map((row) => [row.item.id, row]));
  return [...new Set(input.rankedIds)]
    .flatMap((id) => {
      const row = byId.get(id);
      return row && !popularHidden(row.item, row.category) ? [row] : [];
    })
    .slice(0, limit)
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

/** What the owner kept out of «Lo más pedido» in this menu: products (outside a hidden category) and categories. */
export function popularExclusions(rows: readonly IPopularRow[]): {
  items: IPopularRow[];
  categories: { category: ICategory; itemCount: number }[];
} {
  const categories = new Map<number, { category: ICategory; itemCount: number }>();
  const items: IPopularRow[] = [];
  for (const row of rows) {
    if (row.category?.popularHidden) {
      const entry = categories.get(row.category.id) ?? {
        category: row.category,
        itemCount: 0,
      };
      entry.itemCount += 1;
      categories.set(row.category.id, entry);
    } else if (row.item.popularHidden) items.push(row);
  }
  return { items, categories: [...categories.values()] };
}
